import React, {useMemo, useState} from 'react';
import {Box, ClickAwayListener, styled} from '@mui/material';
import {
  designSystemToken,
  Icon,
  Label,
  Tooltip,
} from '@lightricks/react-design-system';
import {FREE_TRIAL_TOKEN_QUANTITY} from '@contentlab/config/constants';
import useOpenPricing, {
  OPEN_PRICING_TYPE,
} from '@contentlab/hooks/use-open-pricing';
import useTrialUpgradeModal from '@contentlab/hooks/use-trial-upgrade-modal/useTrialUpgradeModal';
import {FreeTrialEndModalReason} from '@contentlab/stores/freeTrialEndModalStore';
import getActiveSubscription from '@contentlab/utils/getActiveSubscription';
import {
  getPlanTokenQuantity,
  getPlanVariant,
  getTokenBalance,
  getTokenRenewalDate,
  isFreePlan,
  isTrialPlan,
} from '@contentlab/views/contentlab/pricing/utils';
import {CONTENT_STUDIO_PRICING_SOURCES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import useCurrentSubscriptionQuery from '@/hooks/content-studio/use-current-subscription-query';
import TooltipContent from './TooltipContent';

interface CreditBoxProps {
  onOpen?: () => void;
  onClose?: () => void;
}

interface BalanceProps {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  tokensBalance: string;
  isFreeTrial: boolean;
}

const flashFilledIcon = '/assets/svg/icon-flash-filled.svg';

const ButtonContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const BalanceContainer = styled(Box, {
  shouldForwardProp: (prop: string) => prop !== 'isFreeTrial',
})<{isFreeTrial: boolean}>(({isFreeTrial}) => ({
  'display': 'inline-flex',
  'padding': '8px',
  'alignItems': 'center',
  'gap': '10px',
  'borderRadius': '999px',
  '&:hover': {
    cursor: 'pointer',
  },
  ...(isFreeTrial && {
    boxShadow:
      '0 4px 12px -2px rgba(24, 39, 75, 0.08), 0 0 4px 0 rgba(24, 39, 75, 0.02)',
  }),
}));

function Balance({onClick, tokensBalance, isFreeTrial}: BalanceProps) {
  return (
    <BalanceContainer onClick={onClick} isFreeTrial={isFreeTrial}>
      {isFreeTrial ? (
        <Icon name="Features-Gift-Before" size="small" appearance="brand" />
      ) : (
        <img src={flashFilledIcon} alt="credits" />
      )}
      {isFreeTrial ? (
        <Label size="sm" color={designSystemToken('semantic.fg.brand')}>
          {translate('free credits left', {credits: tokensBalance})}
        </Label>
      ) : (
        <Label size="sm">
          {tokensBalance === '0'
            ? translate('Out of credits')
            : translate('Credits left', {credits: tokensBalance})}
        </Label>
      )}
    </BalanceContainer>
  );
}

export default function CreditBox(props: CreditBoxProps) {
  const {onOpen, onClose} = props;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const {data, isFetching, refetch} = useCurrentSubscriptionQuery();
  const openPricing = useOpenPricing();
  const openTrialUpgradeModal = useTrialUpgradeModal();
  const activeSubscription = getActiveSubscription(data?.subscriptions);
  const isFreeTrial = isTrialPlan(activeSubscription);
  const formattedTokensBalance = useMemo(() => {
    return new Intl.NumberFormat('en-US').format(
      getTokenBalance(activeSubscription) ?? 0
    );
  }, [activeSubscription]);

  const handleTooltipClose = () => {
    if (!isTooltipOpen) {
      return;
    }
    setIsTooltipOpen(false);
    onClose?.();
  };

  const handleShowBalance = () => {
    if (!isTooltipOpen) {
      refetch();
    }
    setIsTooltipOpen(!isTooltipOpen);
    if (isTooltipOpen) {
      onClose?.();
    } else {
      onOpen?.();
    }
  };

  const handleOpenPricing = () => {
    const openType = isFreePlan(activeSubscription)
      ? OPEN_PRICING_TYPE.REDIRECT
      : OPEN_PRICING_TYPE.MODAL;
    if (isFreeTrial) {
      openTrialUpgradeModal({
        source: 'credit_box',
        reason: FreeTrialEndModalReason.UPGRADE,
      });
    } else {
      openPricing(
        openType,
        CONTENT_STUDIO_PRICING_SOURCES.TOOL_BAR_UPGRADE_BUTTON
      );
    }
    handleTooltipClose();
  };

  let tokensRenewalDate = getTokenRenewalDate(activeSubscription);
  if (
    isFreeTrial &&
    activeSubscription?.renewAtMs &&
    activeSubscription?.createdAtMs
  ) {
    const trialPeriod =
      activeSubscription.renewAtMs - activeSubscription.createdAtMs;
    // We do this to show a static days of the trial period
    // and not the remaining days of the trial period
    tokensRenewalDate = new Date(Date.now() + trialPeriod);
  }
  // TODO: This should be removed once we have a way to get the free trial token quantity from the backend
  let tokensRenewalAmount = getPlanTokenQuantity(activeSubscription?.plan);
  if (isFreeTrial) {
    tokensRenewalAmount = FREE_TRIAL_TOKEN_QUANTITY;
  }

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <ButtonContainer>
        <Tooltip
          open={isTooltipOpen}
          onClose={handleTooltipClose}
          title={
            <TooltipContent
              isLoading={isFetching || !activeSubscription}
              tokensBalance={formattedTokensBalance}
              tokensRenewalDate={tokensRenewalDate}
              tokensRenewalAmount={tokensRenewalAmount}
              plan={getPlanVariant(activeSubscription)}
              handleSeeAllPlansClick={handleOpenPricing}
              isAutoRenewEnabled={activeSubscription?.isAutoRenewEnabled}
            />
          }
          placement="bottom-end"
          arrow
          disableFocusListener
          disableHoverListener
        >
          <div>
            <Balance
              onClick={handleShowBalance}
              tokensBalance={formattedTokensBalance}
              isFreeTrial={isFreeTrial}
            />
          </div>
        </Tooltip>
      </ButtonContainer>
    </ClickAwayListener>
  );
}
